import { useEffect, useRef, useState, useCallback } from "react";
import { Calendar } from "./ui/calendar";
import { Calendar as CalIcon, X } from "lucide-react";
import { useFormat } from "../contexts/PrefsContext";
import { usePrefs } from "../contexts/PrefsContext";

/* ---------- helpers ---------- */
// Parse "YYYY-MM-DD" into a local Date (avoids UTC drift). Returns null on falsy.
export function parseYmd(s) {
    if (!s) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (!m) return null;
    return new Date(+m[1], +m[2] - 1, +m[3]);
}
// Format a Date as local "YYYY-MM-DD".
export function toYmd(d) {
    if (!d || isNaN(d)) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
// Parse "YYYY-MM-DDTHH:mm" into a local Date.
function parseLocalDt(s) {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d) ? null : d;
}
function toLocalDt(d, hhmm) {
    if (!d) return "";
    const [h, mm] = (hhmm || "00:00").split(":");
    const out = new Date(d);
    out.setHours(Number(h) || 0, Number(mm) || 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${out.getFullYear()}-${pad(out.getMonth() + 1)}-${pad(out.getDate())}T${pad(out.getHours())}:${pad(out.getMinutes())}`;
}

/* ---------- shared popover frame ---------- */
function Popover({ open, anchorRef, onClose, children }) {
    const panelRef = useRef(null);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (!panelRef.current || !anchorRef.current) return;
            if (!panelRef.current.contains(e.target) && !anchorRef.current.contains(e.target)) onClose();
        };
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open, anchorRef, onClose]);
    if (!open) return null;
    return (
        <div ref={panelRef}
             className="absolute z-50 mt-1 left-0 bg-white rounded-xl border border-[rgba(26,19,24,0.12)] shadow-xl p-1"
             data-testid="datepicker-popover">
            {children}
        </div>
    );
}

/* ---------- DatePicker (YYYY-MM-DD) ---------- */
export default function DatePicker({
    value, onChange, placeholder = "Pick a date", disabled = false,
    required = false, testid = "datepicker", className = "",
    fromYear = 1990, toYear = new Date().getFullYear() + 10,
    clearable = true, name,
}) {
    const { prefs } = usePrefs();
    const fmt = useFormat();
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const selected = parseYmd(value);

    const pick = useCallback((d) => {
        onChange?.(d ? toYmd(d) : "");
        setOpen(false);
    }, [onChange]);

    const clear = (e) => {
        e.stopPropagation();
        onChange?.("");
    };

    const label = selected ? fmt.fmtDate(selected) : "";
    return (
        <div className={`relative ${className}`} ref={anchorRef}>
            <button type="button" disabled={disabled}
                    onClick={() => !disabled && setOpen(o => !o)}
                    data-testid={`${testid}-button`}
                    aria-label={placeholder}
                    aria-expanded={open}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] bg-white/60 hover:border-[var(--brand-purple)]/50 focus:outline-none focus:border-[var(--brand-purple)] transition text-left text-sm disabled:opacity-50">
                <CalIcon size={14} className="text-[var(--text-ink)]/55 shrink-0" aria-hidden />
                <span className={`flex-1 truncate ${selected ? "" : "text-[var(--text-ink)]/45"}`} data-testid={`${testid}-display`}>
                    {selected ? label : placeholder}
                </span>
                {selected && clearable && !disabled && (
                    <span role="button" tabIndex={0}
                          onClick={clear}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") clear(e); }}
                          aria-label="Clear date"
                          data-testid={`${testid}-clear`}
                          className="text-[var(--text-ink)]/40 hover:text-[var(--text-ink)] cursor-pointer">
                        <X size={12} />
                    </span>
                )}
            </button>
            {/* Hidden input keeps native form behaviour (HTML5 required, name binding) */}
            <input type="hidden" name={name} value={value || ""} required={required} />
            <Popover open={open} anchorRef={anchorRef} onClose={() => setOpen(false)}>
                <Calendar
                    mode="single"
                    selected={selected || undefined}
                    onSelect={pick}
                    initialFocus
                    weekStartsOn={prefs.week_start ?? 1}
                    captionLayout="dropdown-buttons"
                    fromYear={fromYear}
                    toYear={toYear}
                    defaultMonth={selected || new Date()}
                />
            </Popover>
        </div>
    );
}

/* ---------- DateTimePicker (YYYY-MM-DDTHH:mm) ---------- */
export function DateTimePicker({
    value, onChange, placeholder = "Pick a date & time",
    disabled = false, testid = "datetimepicker", className = "",
    fromYear = 1990, toYear = new Date().getFullYear() + 10, name,
}) {
    const { prefs } = usePrefs();
    const fmt = useFormat();
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const selected = parseLocalDt(value);
    const [time, setTime] = useState(selected
        ? `${String(selected.getHours()).padStart(2, "0")}:${String(selected.getMinutes()).padStart(2, "0")}`
        : "09:00");

    useEffect(() => {
        if (selected) {
            setTime(`${String(selected.getHours()).padStart(2, "0")}:${String(selected.getMinutes()).padStart(2, "0")}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const pickDay = (d) => {
        if (!d) return onChange?.("");
        onChange?.(toLocalDt(d, time));
    };
    const pickTime = (t) => {
        setTime(t);
        const base = selected || new Date();
        onChange?.(toLocalDt(base, t));
    };

    const label = selected ? `${fmt.fmtDate(selected)} · ${fmt.fmtTime(selected)}` : "";

    return (
        <div className={`relative ${className}`} ref={anchorRef}>
            <button type="button" disabled={disabled}
                    onClick={() => !disabled && setOpen(o => !o)}
                    data-testid={`${testid}-button`}
                    aria-expanded={open}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] bg-white/60 hover:border-[var(--brand-purple)]/50 focus:outline-none focus:border-[var(--brand-purple)] transition text-left text-sm disabled:opacity-50">
                <CalIcon size={14} className="text-[var(--text-ink)]/55 shrink-0" aria-hidden />
                <span className={`flex-1 truncate ${selected ? "" : "text-[var(--text-ink)]/45"}`} data-testid={`${testid}-display`}>
                    {selected ? label : placeholder}
                </span>
            </button>
            <input type="hidden" name={name} value={value || ""} />
            <Popover open={open} anchorRef={anchorRef} onClose={() => setOpen(false)}>
                <Calendar
                    mode="single"
                    selected={selected || undefined}
                    onSelect={pickDay}
                    initialFocus
                    weekStartsOn={prefs.week_start ?? 1}
                    captionLayout="dropdown-buttons"
                    fromYear={fromYear}
                    toYear={toYear}
                    defaultMonth={selected || new Date()}
                />
                <div className="flex items-center gap-2 px-3 py-2 border-t border-[rgba(26,19,24,0.08)]">
                    <span className="text-xs text-[var(--text-ink)]/55">Time</span>
                    <input type="time" value={time}
                           onChange={(e) => pickTime(e.target.value)}
                           data-testid={`${testid}-time`}
                           className="flex-1 px-2 py-1 rounded-lg border border-[rgba(26,19,24,0.15)] text-sm" />
                    <button type="button" onClick={() => setOpen(false)}
                            data-testid={`${testid}-done`}
                            className="btn-primary text-xs">Done</button>
                </div>
            </Popover>
        </div>
    );
}
